import { useTranslation } from 'react-i18next';

import {
  CHANNEL_NAME_MAP,
  PROTOCOL,
  TChannelProtocol,
  TChannelTypeGroup,
} from '@/entities/channel';
import { Stack } from '@/shared/ui';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { SvgIcon } from '@/shared/ui/SvgIcon';

import { ChannelCard } from './ChannelCard/ChannelCard';

type TSelectChannelsDialogProps = TDialogBase & {
  onSelected: (protocol: TChannelProtocol) => void;
  onBack: () => void;
  channelType?: TChannelTypeGroup;
};

const wideLogoSx = { width: 88, height: 24 } as const;
const vkLogoSx = { width: 40, height: 40 } as const;

export const SelectChannelsDialog = ({
  onClose,
  onBack,
  onSelected,
  channelType,
  ...otherProps
}: TSelectChannelsDialogProps) => {
  const { t } = useTranslation();

  const createOnClickHandler = (protocol: TChannelProtocol) => () => {
    onSelected(protocol);
  };

  const renderContent = () => {
    switch (channelType) {
      case 'telegram': {
        return (
          <>
            <ChannelCard
              variant="secondary"
              icon={<SvgIcon name="telegram" fontSize="inherit" />}
              label={CHANNEL_NAME_MAP[PROTOCOL.TELEGRAM_PERSONAL]}
              caption={t('channels:tgPersonal.caption')}
              onClick={createOnClickHandler(PROTOCOL.TELEGRAM_PERSONAL)}
            />
            <ChannelCard
              variant="secondary"
              icon={<SvgIcon name="telegramBot" fontSize="inherit" />}
              label={CHANNEL_NAME_MAP[PROTOCOL.TELEGRAM_BOT]}
              caption={t('channels:tgBot.caption')}
              onClick={createOnClickHandler(PROTOCOL.TELEGRAM_BOT)}
            />
          </>
        );
      }
      case 'whatsapp': {
        return (
          <>
            <ChannelCard
              variant="secondary"
              icon={<SvgIcon name="whatsapp" fontSize="inherit" />}
              label={CHANNEL_NAME_MAP[PROTOCOL.WHATSAPP_GRAY]}
              caption={t('channels:whatsAppPersonal.caption')}
              onClick={createOnClickHandler(PROTOCOL.WHATSAPP_GRAY)}
            />
            {/* <ChannelCard */}
            {/*  variant="secondary" */}
            {/*  icon={<SvgIcon name="whatsapp-business" fontSize="inherit" />} */}
            {/*  label={CHANNEL_NAME_MAP[PROTOCOL.WABA]} */}
            {/*  caption={t('channels:waba.caption')} */}
            {/*  onClick={createOnClickHandler(PROTOCOL.WABA)} */}
            {/* /> */}
          </>
        );
      }
      case 'max': {
        return (
          <>
            <ChannelCard
              variant="secondary"
              icon={<SvgIcon name="max" fontSize="inherit" />}
              label="MAX Direct"
              caption={t('channels:max.caption')}
              onClick={createOnClickHandler(PROTOCOL.MAX)}
            />
            <ChannelCard
              variant="secondary"
              icon={<SvgIcon name="max" fontSize="inherit" />}
              label={CHANNEL_NAME_MAP[PROTOCOL.MAX_PERSONAL]}
              caption={t('channels:maxPersonal.caption')}
              onClick={createOnClickHandler(PROTOCOL.MAX_PERSONAL)}
            />
          </>
        );
      }
      case 'avito': {
        return (
          <ChannelCard
            variant="secondary"
            icon={<SvgIcon name="avito" fontSize="inherit" />}
            label={CHANNEL_NAME_MAP[PROTOCOL.AVITO]}
            caption={t('channels:avito.caption')}
            onClick={createOnClickHandler(PROTOCOL.AVITO)}
          />
        );
      }
      case 'wildberries': {
        return (
          <ChannelCard
            variant="secondary"
            icon={
              <SvgIcon
                name="wildberries"
                viewBox="0 0 500 75.1"
                sx={wideLogoSx}
              />
            }
            label={CHANNEL_NAME_MAP[PROTOCOL.WILDBERRIES]}
            caption={t('channels:wildberries.caption')}
            onClick={createOnClickHandler(PROTOCOL.WILDBERRIES)}
          />
        );
      }
      case 'ozon': {
        return (
          <ChannelCard
            variant="secondary"
            icon={
              <SvgIcon name="ozon" viewBox="0 0 485 106" sx={wideLogoSx} />
            }
            label={CHANNEL_NAME_MAP[PROTOCOL.OZON]}
            caption={t('channels:ozon.caption')}
            onClick={createOnClickHandler(PROTOCOL.OZON)}
          />
        );
      }
      case 'vk': {
        return (
          <ChannelCard
            variant="secondary"
            icon={<SvgIcon name="vk" viewBox="0 0 48 48" sx={vkLogoSx} />}
            label={CHANNEL_NAME_MAP[PROTOCOL.VK]}
            caption={t('channels:vk.caption')}
            onClick={createOnClickHandler(PROTOCOL.VK)}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Dialog
      onClose={onClose}
      title={t('channels:title')}
      leftButtonSlot={
        <Button variant="outlined" onClick={onBack}>
          {t('channels:buttons.moveBack')}
        </Button>
      }
      {...otherProps}
    >
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', maxWidth: '500px' }}
      >
        {renderContent()}
      </Stack>
    </Dialog>
  );
};
